import React, { useCallback, useEffect, useState, useRef } from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  type useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Box,
  Text
} from "@chakra-ui/react"

interface NewFMPModalProps {
  onCreateCallBack: (projectName: string) => void
  defaultProjectName?: string
  isOpen: ReturnType<typeof useDisclosure>["isOpen"]
  onClose: ReturnType<typeof useDisclosure>["onClose"]
  headerTitle: string
  mainButtonText?: string
}

export const NewFMPModal: React.FC<NewFMPModalProps> = ({ isOpen, onClose, onCreateCallBack, headerTitle, mainButtonText, defaultProjectName }) => {
  const [projectName, setProjectName] = useState("")
  const initialFocusRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (defaultProjectName) {
      setProjectName(defaultProjectName)
    }
  }, [defaultProjectName])

  const handleOnClose = (): void => {
    // Prevent focus restoration by blurring any focused element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    onClose()
  }

  const handleOnCreate = useCallback((): void => {
    // Prevent focus restoration by blurring any focused element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    onClose()
    onCreateCallBack(projectName)
  }, [projectName, onCreateCallBack, onClose])

  const handleProjectNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const element = e.target ?? null
    const value = element?.value ?? ""
    if (element) {
      setProjectName(value)
    }
  }, [])

  const formOnSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      handleOnCreate()
    },
    [handleOnCreate]
  )

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleOnClose}
        initialFocusRef={initialFocusRef}
        finalFocusRef={undefined}
        returnFocusOnClose={false}
        blockScrollOnMount={false}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg="accent" color="white" w="257px">
          <ModalCloseButton h="24px" />
          <form onSubmit={formOnSubmitHandler}>
            <ModalBody px="4" mt="0" pt="0">
              <Text mt="8" variant="RBodyBold" textAlign="center" pb="2">
                {headerTitle}
              </Text>
              <FormControl>
                <FormLabel mb="1" pl="2">
                  Project Name
                </FormLabel>
                <Input ref={initialFocusRef} borderRadius={"4px"} tabIndex={1} value={projectName} onChange={handleProjectNameChange} />
              </FormControl>
            </ModalBody>

            <ModalFooter justifyContent="flexStart">
              <Box display="grid" gridTemplateColumns={"repeat(2, auto)"} gridGap="2">
                <Button
                  tabIndex={3}
                  variant="linkButton"
                  fontWeight="400"
                  color="white"
                  pl="4"
                  onClick={handleOnClose}
                  gridColumnStart="1"
                  justifySelf="flex-start"
                >
                  Cancel
                </Button>
                <Button tabIndex={2} type="submit" variant="Button1RegularOutline" gridColumnStart="2">
                  {mainButtonText ?? "Create"}
                </Button>
              </Box>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  )
}

export default NewFMPModal
